import React from 'react';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dumbbell, CheckCircle2 } from 'lucide-react';

const CLUB_TYPES = [
  { value: 'yoga_pilates', label: 'Yoga / Pilates' },
  { value: 'cycling_spin', label: 'Cycling / Spin' },
  { value: 'hiit_group', label: 'HIIT / Group Training' },
  { value: 'strength', label: 'Strength & Conditioning' },
  { value: 'boxing_kickboxing', label: 'Boxing / Kickboxing Fitness' },
  { value: 'dance_barre', label: 'Dance / Barre' },
  { value: 'personal_training', label: 'Personal Training Studio' },
  { value: 'wellness_recovery', label: 'Wellness / Recovery Studio' },
  { value: 'other', label: 'Other' }
];

const SERVICES = [
  { value: 'group_classes', label: 'Group Classes' },
  { value: 'personal_training', label: '1:1 Personal Training' },
  { value: 'open_gym', label: 'Open Gym' },
  { value: 'youth_classes', label: 'Youth Classes' },
  { value: 'locker_rooms', label: 'Locker Rooms / Showers' },
  { value: 'sauna_cold_plunge', label: 'Sauna / Cold Plunge' },
  { value: 'other', label: 'Other' }
];

const FitnessClubType = () => {
  const { formData, updateFormData, nextStep } = useFitnessWizard();

  const handleSubmit = (e) => {
    e.preventDefault();
    nextStep();
  };

  const handleServiceToggle = (service) => {
    const current = formData.fitness_services || [];
    if (current.includes(service)) {
      updateFormData({ fitness_services: current.filter(s => s !== service) });
    } else {
      updateFormData({ fitness_services: [...current, service] });
    }
  };

  const isServiceSelected = (service) => {
    return (formData.fitness_services || []).includes(service);
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <Dumbbell className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Fitness Club Type & Services</CardTitle>
            <CardDescription className="text-white/60">
              Tell us about your fitness business type and offerings
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Club Type Selection */}
          <div>
            <Label className="text-base font-semibold">What type of boutique fitness business are you? *</Label>
            <p className="text-sm text-slate-600 mb-3">Select the option that best describes your business</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CLUB_TYPES.map((type) => (
                <div
                  key={type.value}
                  data-testid={`club-type-${type.value}`}
                  onClick={() => updateFormData({ fitness_club_type: type.value })}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.fitness_club_type === type.value
                      ? 'border-[#7B6FE8] bg-[#EEF0FF]'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${
                      formData.fitness_club_type === type.value ? 'text-[#1A1A1A]' : 'text-slate-700'
                    }`}>
                      {type.label}
                    </span>
                    {formData.fitness_club_type === type.value && (
                      <CheckCircle2 className="h-5 w-5 text-[#7B6FE8]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {formData.fitness_club_type === 'other' && (
              <div className="mt-3">
                <Label htmlFor="fitness_club_type_other">Please specify *</Label>
                <Input
                  id="fitness_club_type_other"
                  data-testid="club-type-other-input"
                  value={formData.fitness_club_type_other}
                  onChange={(e) => updateFormData({ fitness_club_type_other: e.target.value })}
                  required={formData.fitness_club_type === 'other'}
                  className="mt-1.5"
                  placeholder="Describe your fitness business type"
                />
              </div>
            )}
          </div>

          {/* Services Selection */}
          <div>
            <Label className="text-base font-semibold">Which services do you offer? *</Label>
            <p className="text-sm text-slate-600 mb-3">Select all that apply</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SERVICES.map((service) => (
                <div
                  key={service.value}
                  data-testid={`service-${service.value}`}
                  onClick={() => handleServiceToggle(service.value)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isServiceSelected(service.value)
                      ? 'border-[#4ECDC4] bg-[#EEF0FF]'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${
                      isServiceSelected(service.value) ? 'text-[#1A1A1A]' : 'text-slate-700'
                    }`}>
                      {service.label}
                    </span>
                    {isServiceSelected(service.value) && (
                      <CheckCircle2 className="h-5 w-5 text-[#4ECDC4]" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {isServiceSelected('other') && (
              <div className="mt-3">
                <Label htmlFor="fitness_services_other">Please specify other services</Label>
                <Input
                  id="fitness_services_other"
                  data-testid="services-other-input"
                  value={formData.fitness_services_other}
                  onChange={(e) => updateFormData({ fitness_services_other: e.target.value })}
                  className="mt-1.5"
                  placeholder="Describe other services you offer"
                />
              </div>
            )}
          </div>

          <ThemedButton 
            type="submit"
            data-testid="fitness-club-type-next-btn"
            disabled={!formData.fitness_club_type || formData.fitness_services.length === 0}
            variant="wizard" size="full"
          >
            Next: Locations & Facility Details
          </ThemedButton>
        </form>
      </CardContent>
    </Card>
  );
};

export default FitnessClubType;
